import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LOCALES, type Locale } from '@h3eromap/i18n';
import { ClientTour, resetClientTour } from '@/components/client-tour';
import { useAuth } from '@/app/auth-context';
import { setLocale } from '@/lib/i18n';
import * as api from '@/lib/api-client';
import { updateMyLanguage } from '@/lib/api-client';
import {
  getSessionLifetimeDays,
  setSessionLifetimeDays,
  SESSION_LIFETIME_OPTIONS,
  type SessionLifetimeDays,
} from '@/lib/session-lifetime';

export function SettingsPage(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [replayOpen, setReplayOpen] = useState(false);

  return (
    <section className="max-w-2xl mx-auto px-s5 py-s7">
      <h1 className="font-display text-display-lg text-ink">{t('settings.title')}</h1>

      <section className="mt-s6 bg-bone-2 rounded-lg p-s5 border border-bone-3">
        <h2 className="font-display text-xl text-ink">
          {t('settings.languageSection.label')}
        </h2>
        <p className="mt-s2 text-sm text-gray-2">{t('settings.languageSection.hint')}</p>
        <div className="mt-s4">
          <LanguageSaveSection />
        </div>
      </section>

      {user?.role === 'client' && (
        <section className="mt-s5 bg-bone-2 rounded-lg p-s5 border border-bone-3">
          <h2 className="font-display text-xl text-ink">
            {t('settings.tourSection.label')}
          </h2>
          <p className="mt-s2 text-sm text-gray-2">{t('settings.tourSection.hint')}</p>
          <div className="mt-s4">
            <button
              type="button"
              onClick={() => {
                resetClientTour(user.id);
                setReplayOpen(true);
              }}
              className="btn-primary text-sm"
              data-testid="settings-replay-tour"
            >
              {t('settings.tourSection.replay')}
            </button>
          </div>
        </section>
      )}

      {user?.role === 'client' && replayOpen && (
        <ClientTour
          userId={user.id}
          forceShow
          onClose={() => setReplayOpen(false)}
        />
      )}

      {user?.role === 'client' && <StayLoggedInSection />}
      {user?.role === 'client' && <PrivacyConsentSection />}
      {user?.role === 'client' && <DataExportSection />}
      {user?.role === 'client' && <ErasureSection />}
    </section>
  );
}

/* ─────────────────────────────  Language (#9 — explicit Save)  ───────────────────────────── */

/**
 * #9 — the language preference now commits on an explicit Save (was a
 * fire-and-forget switch). The select holds a pending choice; Save applies
 * it to the page, persists it to the server, and updates the session so the
 * on-login language sync keeps it across reloads.
 */
function LanguageSaveSection(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { user, accessToken, setUserLanguage } = useAuth();
  const current = (i18n.resolvedLanguage ?? 'de') as Locale;
  const [pending, setPending] = useState<Locale>(current);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Realign the pending choice if the active language changes elsewhere
  // (e.g. the on-login sync) while this view is mounted and untouched.
  useEffect(() => {
    setPending(current);
  }, [current]);

  const dirty = pending !== current;

  const onSave = async (): Promise<void> => {
    if (!dirty) return;
    setSaving(true);
    try {
      await setLocale(pending);
      if (user && (pending === 'de' || pending === 'en')) {
        setUserLanguage(pending);
        await updateMyLanguage(accessToken ?? '', pending);
      }
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch {
      /* the UI language already switched; the server persist is best-effort */
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-s3">
      <label className="flex items-center gap-s2 text-sm text-gray-2">
        <span className="sr-only">{t('common.labels.language')}</span>
        <select
          aria-label={t('common.labels.language')}
          className="bg-bone-1 border border-bone-3 rounded-sm px-s3 py-s2 focus:outline-none focus:ring-1 focus:ring-flame text-ink"
          value={pending}
          onChange={(e) => setPending(e.target.value as Locale)}
          data-testid="settings-language-select"
        >
          {LOCALES.map((code) => (
            <option key={code} value={code}>
              {t(`common.languages.${code}`)}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        onClick={() => void onSave()}
        disabled={!dirty || saving}
        data-testid="settings-language-save"
        className={
          dirty
            ? 'btn-primary text-sm'
            : 'rounded-md px-s4 py-s2 text-sm font-semibold text-gray-2 bg-bone-3 cursor-default'
        }
      >
        {saved
          ? `✓ ${t('settings.languageSection.saved')}`
          : saving
            ? t('settings.languageSection.saving')
            : t('settings.languageSection.save')}
      </button>
    </div>
  );
}

/* ─────────────────────────────  Stay logged in  ───────────────────────────── */

/**
 * Client-only "Stay logged in for N days" preference. The cap is
 * enforced client-side by `auth-context`'s periodic check. The
 * server-side max stays at 30 days (refresh-token TTL).
 */
function StayLoggedInSection(): JSX.Element {
  const { t } = useTranslation();
  const [days, setDays] = useState<SessionLifetimeDays>(() => getSessionLifetimeDays());

  const onChange = (value: SessionLifetimeDays): void => {
    setSessionLifetimeDays(value);
    setDays(value);
  };

  return (
    <section
      className="mt-s5 bg-bone-2 rounded-lg p-s5 border border-bone-3"
      data-testid="settings-stay-logged-in"
    >
      <h2 className="font-display text-xl text-ink">
        {t('settings.stayLoggedIn.label')}
      </h2>
      <p className="mt-s2 text-sm text-gray-2">{t('settings.stayLoggedIn.hint')}</p>
      <fieldset className="mt-s4 flex flex-wrap gap-s3">
        <legend className="sr-only">{t('settings.stayLoggedIn.label')}</legend>
        {SESSION_LIFETIME_OPTIONS.map((value) => {
          const checked = days === value;
          return (
            <label
              key={value}
              className={`flex cursor-pointer items-center gap-s2 rounded-md border px-s3 py-s2 text-sm ${
                checked
                  ? 'border-flame-deep bg-flame-glow text-flame-deep'
                  : 'border-bone-3 bg-bone-1 text-ink'
              }`}
            >
              <input
                type="radio"
                name="stay-logged-in"
                value={value}
                checked={checked}
                onChange={() => onChange(value)}
                className="accent-flame-ember"
                data-testid={`settings-stay-logged-in-${value}`}
              />
              {t('settings.stayLoggedIn.days', { count: value })}
            </label>
          );
        })}
      </fieldset>
      <p className="mt-s3 text-xs text-gray-1">{t('settings.stayLoggedIn.footnote')}</p>
    </section>
  );
}

/* ─────────────────────────────  Erasure (GDP-078)  ───────────────────────────── */

function ErasureSection(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken, user } = useAuth();
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scheduledFor, setScheduledFor] = useState<string | null>(null);
  const [hydrating, setHydrating] = useState(true);

  const requiredConfirm = t('settings.erasureSection.confirmPhrase');

  // Hydrate the erasure state from the server on mount. Without this,
  // `scheduledFor` is always null on a cold page load, even when the
  // client has an in-flight deletion request — that's why "the client
  // cannot see if the deletion was requested once he logged off and on
  // again". The server is the source of truth (column on clients table).
  useEffect(() => {
    const clientId = user?.clientId;
    if (!clientId) {
      setHydrating(false);
      return;
    }
    let active = true;
    void (async () => {
      try {
        const self = await api.getClientSelf(accessToken, clientId);
        if (active) setScheduledFor(self.erasure_scheduled_for);
      } catch {
        // Hydration failures aren't user-blocking — the request/cancel
        // flow still works; we just default to "no pending request" UI.
        // A persistent fetch error would surface on the first action.
      } finally {
        if (active) setHydrating(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, user?.clientId]);

  const refetch = async (): Promise<void> => {
    if (!user?.clientId) return;
    try {
      const self = await api.getClientSelf(accessToken, user.clientId);
      setScheduledFor(self.erasure_scheduled_for);
    } catch {
      // Tolerate refetch failure — caller already updated state optimistically.
    }
  };

  const onRequest = async (): Promise<void> => {
    if (!user?.clientId) return;
    if (confirmText.trim() !== requiredConfirm) {
      setError(t('settings.erasureSection.errors.confirmPhraseMismatch'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.requestErasure(accessToken, user.clientId);
      setScheduledFor(result.erasure_scheduled_for);
      setConfirmText('');
      // Belt-and-suspenders: refetch from the server so a refresh of
      // the same tab also reflects the new state.
      await refetch();
    } catch {
      setError(t('settings.erasureSection.errors.requestFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const onCancel = async (): Promise<void> => {
    if (!user?.clientId) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.cancelErasure(accessToken, user.clientId);
      // The previous implementation set local state to null and trusted
      // that. Now we re-read from the server so the UI is anchored to
      // the row's actual state — if the cancel endpoint silently
      // no-op'd (e.g., race with the finalizer cron), the UI surfaces
      // the truth instead of a stale "cancelled" view.
      await refetch();
    } catch {
      setError(t('settings.erasureSection.errors.cancelFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="mt-s5 bg-bone-2 rounded-lg p-s5 border-2 border-flame-deep/40"
      data-testid="settings-erasure-section"
    >
      <h2 className="font-display text-xl text-flame-deep">
        {t('settings.erasureSection.label')}
      </h2>
      <p className="mt-s2 text-sm text-gray-2">{t('settings.erasureSection.hint')}</p>

      {hydrating ? (
        <p className="mt-s4 text-sm text-gray-2" data-testid="settings-erasure-loading">
          {t('common.loading')}
        </p>
      ) : scheduledFor ? (
        <div className="mt-s4 p-s4 bg-flame/10 rounded">
          <p className="text-sm text-ink font-medium">
            {t('settings.erasureSection.scheduled', { date: scheduledFor.slice(0, 10) })}
          </p>
          <p className="mt-s2 text-xs text-gray-2">
            {t('settings.erasureSection.cancelHint')}
          </p>
          <button
            type="button"
            onClick={() => void onCancel()}
            disabled={submitting}
            className="mt-s3 btn-primary text-sm"
            data-testid="settings-erasure-cancel"
          >
            {submitting
              ? t('settings.erasureSection.cancelling')
              : t('settings.erasureSection.cancelButton')}
          </button>
        </div>
      ) : (
        <div className="mt-s4 space-y-s3">
          <p className="text-sm text-ink">
            {t('settings.erasureSection.confirmInstruction', { phrase: requiredConfirm })}
          </p>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="input-field"
            placeholder={requiredConfirm}
            data-testid="settings-erasure-confirm"
          />
          <button
            type="button"
            onClick={() => void onRequest()}
            disabled={submitting || confirmText.trim() !== requiredConfirm}
            className="btn-primary text-sm bg-flame-deep"
            data-testid="settings-erasure-request"
          >
            {submitting
              ? t('settings.erasureSection.requesting')
              : t('settings.erasureSection.requestButton')}
          </button>
        </div>
      )}

      {error && (
        <p className="mt-s3 text-sm text-flame-deep" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

/* ─────────────────────────────  Data export (GDP-077)  ───────────────────────────── */

function DataExportSection(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken, user } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onExport = async (): Promise<void> => {
    if (!user?.clientId) return;
    setDownloading(true);
    setError(null);
    try {
      const result = await api.requestDataExport(accessToken, user.clientId);
      // Bundle the JSON + Markdown summary into a downloadable Blob.
      const jsonStr = JSON.stringify(result.export, null, 2);
      triggerDownload(
        new Blob([jsonStr], { type: 'application/json' }),
        `h3eromap-export.json`,
      );
      triggerDownload(
        new Blob([result.summary_md], { type: 'text/markdown' }),
        `h3eromap-export-summary.md`,
      );
    } catch {
      setError(t('settings.dataExportSection.errors.failed'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section
      className="mt-s5 bg-bone-2 rounded-lg p-s5 border border-bone-3"
      data-testid="settings-export-section"
    >
      <h2 className="font-display text-xl text-ink">
        {t('settings.dataExportSection.label')}
      </h2>
      <p className="mt-s2 text-sm text-gray-2">{t('settings.dataExportSection.hint')}</p>
      <div className="mt-s4">
        <button
          type="button"
          onClick={() => void onExport()}
          disabled={downloading || !user?.clientId}
          className="btn-primary text-sm"
          data-testid="settings-export-button"
        >
          {downloading
            ? t('settings.dataExportSection.downloading')
            : t('settings.dataExportSection.download')}
        </button>
      </div>
      {error && (
        <p className="mt-s3 text-sm text-flame-deep" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ─────────────────────────────  Privacy & consent (GDP-076 + GDP-079)  ───────────────────────────── */

function PrivacyConsentSection(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken, user } = useAuth();
  const [snapshot, setSnapshot] = useState<api.ConsentSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<api.ConsentType | null>(null);

  useEffect(() => {
    if (!user) return;
    let active = true;
    setLoading(true);
    void (async () => {
      try {
        const result = await api.getConsent(accessToken);
        if (active) setSnapshot(result);
      } catch {
        if (active) setError(t('settings.privacySection.errors.loadFailed'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, t]);

  const onToggle = async (type: api.ConsentType, granted: boolean): Promise<void> => {
    if (!user || !snapshot) return;
    setSaving(type);
    setError(null);
    try {
      const updated = await api.postConsent(accessToken, type, granted);
      setSnapshot({
        ...snapshot,
        consents: snapshot.consents.map((c) =>
          c.consent_type === type
            ? {
                consent_type: type,
                granted: updated.granted,
                version: updated.version,
                granted_at: updated.granted_at,
                is_current_version: updated.version === snapshot.active_legal_version,
              }
            : c,
        ),
      });
    } catch {
      setError(t('settings.privacySection.errors.saveFailed'));
    } finally {
      setSaving(null);
    }
  };

  return (
    <section
      className="mt-s5 bg-bone-2 rounded-lg p-s5 border border-bone-3"
      data-testid="settings-privacy-section"
    >
      <h2 className="font-display text-xl text-ink">
        {t('settings.privacySection.label')}
      </h2>
      <p className="mt-s2 text-sm text-gray-2">{t('settings.privacySection.hint')}</p>

      {loading && (
        <p className="mt-s4 text-sm text-gray-2" role="status">
          {t('common.loading')}
        </p>
      )}
      {error && (
        <p className="mt-s4 text-sm text-flame-deep" role="alert">
          {error}
        </p>
      )}

      {snapshot && (
        <div className="mt-s4 space-y-s3">
          {snapshot.consents.map((c) => (
            <ConsentRow
              key={c.consent_type}
              consent={c}
              onToggle={onToggle}
              saving={saving === c.consent_type}
            />
          ))}
          <p className="text-xs text-gray-1 mt-s3">
            {t('settings.privacySection.legalVersion', {
              version: snapshot.active_legal_version,
            })}
          </p>
        </div>
      )}
    </section>
  );
}

interface ConsentRowProps {
  consent: api.ConsentState;
  onToggle: (type: api.ConsentType, granted: boolean) => Promise<void>;
  saving: boolean;
}

function ConsentRow(props: ConsentRowProps): JSX.Element {
  const { t } = useTranslation();
  const { consent } = props;
  const required = consent.consent_type === 'data_processing';
  return (
    <div className="flex items-start justify-between gap-s4">
      <div className="flex-1">
        <p className="text-sm font-medium text-ink">
          {t(`settings.privacySection.consents.${consent.consent_type}.label`)}
          {required && <span className="ml-1 text-flame-deep">*</span>}
        </p>
        <p className="text-xs text-gray-1">
          {t(`settings.privacySection.consents.${consent.consent_type}.help`)}
        </p>
        {!consent.is_current_version && (
          <p className="text-xs text-flame-deep mt-1">
            {t('settings.privacySection.staleVersion')}
          </p>
        )}
      </div>
      <label className="flex items-center gap-s2 cursor-pointer">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-bone-3 text-flame focus:ring-flame"
          checked={consent.granted}
          disabled={required || props.saving}
          onChange={(event) => {
            void props.onToggle(consent.consent_type, event.target.checked);
          }}
          data-testid={`settings-consent-${consent.consent_type}`}
        />
        <span className="text-xs text-gray-2">
          {consent.granted
            ? t('settings.privacySection.granted')
            : t('settings.privacySection.notGranted')}
        </span>
      </label>
    </div>
  );
}
